import {observer} from "mobx-react";
import React, {CSSProperties, useRef, useState} from "react";
import styles from "./styles/StatementBlockCS.module.scss";
import BlockTitle from "../_BlockTitle";
import {CSSTransition} from "react-transition-group";
import dynamic from "next/dynamic";
import {getCdnUrl, reactScrollBarsCustomWidth} from "../../../../utils/SchoolBlocksUtilities";
import {buildCssUrlString} from "../../../../utils/StringUtilities";
import ClickableLink from "../../../utilities/ClickableLink";
import Scrollbar from "react-scrollbars-custom";
import Markdown from "../../../utilities/Markdown";
import {EditDropDownItem} from "../EditDropDown";
import FontAwesome from "../../../utilities/FontAwesome";
import classNames from 'classnames';
import ImageComponent from "../../../utilities/ImageComponent";
import variables from "../../../../styles/_variables.module.scss";
import {useStores} from "../../../../stores/useStores";
import CTAButton from "../../../utilities/CTAButton";

export enum StatementBackgroundNames {
    DARK = "dark",
    LIGHT = "light",
    THEME = "theme",
}

type StatementBackground = {
    name: StatementBackgroundNames;
    color: string;
    textColor: string;
};

export type StatementBackgroundObject = {
    [key in StatementBackgroundNames]: StatementBackground;
};

const StatementBlockEditor = dynamic(() => import("./StatementBlockEditor"));

const transitionClassNames = {
    enter: styles.transitionEnter,
    enterActive: styles.transitionEnterActive,
    enterDone: styles.transitionEnterDone,
    exit: styles.transitionExit,
    exitActive: styles.transitionExitActive,
    exitDone: styles.transitionExitDone,
};
const StatementBlock = observer((props: {
    blockObj: IStatementTerrace
    aboveTheFold: boolean,
}) => {
    const {styleStore} = useStores();
    const white = "#FFFFFF";

    const statementBackgroundObject: StatementBackgroundObject = {
        [StatementBackgroundNames.DARK]: {
            name: StatementBackgroundNames.DARK,
            color: "#000",
            textColor: white,
        },
        [StatementBackgroundNames.LIGHT]: {
            name: StatementBackgroundNames.LIGHT,
            color: variables["background-color2"],
            textColor: variables["black"],
        },
        [StatementBackgroundNames.THEME]: {
            name: StatementBackgroundNames.THEME,
            color: styleStore.cssVars.blocks,
            textColor: styleStore.cssVars.blocksTextColor,
        },
    }

    const [editMode, setEditMode] = useState(false);
    const transitionRef = useRef(null);

    const mediaObject = props.blockObj.json_data.statement?.media;
    const mediaObjectUrl: string = mediaObject?.url || "";
    const imgSrcUrl = buildCssUrlString(getCdnUrl(mediaObjectUrl));

    const statementBlockEditorDropDownItems: EditDropDownItem[] = [
        {
            title: 'Edit',
            icon: <FontAwesome prefix={'fas'} fixedWidth className="fa-pencil-alt"/>,
            onClick: () => setEditMode(v => !v),
        },
    ]
    const messageContainerClassName = classNames({
        [styles.messageContainer]: true,
        [styles.messageContainerNoButton]: !props.blockObj.json_data.statement?.buttonData.buttonToggle,
    })

    const statementColor = props.blockObj.json_data.statement?.backgroundColor ||
        StatementBackgroundNames.DARK;

    const textColorIsWhite = statementBackgroundObject[statementColor].textColor === white;

    return (
        <section  style={{
            '--statementBackgroundColor': statementBackgroundObject[statementColor].color,
            '--statementTextColor': statementBackgroundObject[statementColor].textColor,
        } as CSSProperties}
                  className={styles.statementContainer}>
            {editMode ? <StatementBlockEditor
                editMode={editMode}
                setEditMode={setEditMode}
                blockObj={props.blockObj}
                statementBlockEditorDropDownItems={statementBlockEditorDropDownItems}
                statementBackgroundObject={statementBackgroundObject}
            /> : <>
                <div className={styles.statementBackgroundImage}
                     style={mediaObjectUrl ? {backgroundImage: imgSrcUrl} : {}}/>
                <div className={styles.statementGradient}>
                    <BlockTitle blockObj={props.blockObj}
                                editing={editMode}
                                setEditing={setEditMode}
                                aboveTheFold={props.aboveTheFold}
                                blockTitleContainerClassName={styles.blockTitleContainerClassName}
                                dropDownItems={statementBlockEditorDropDownItems}
                                editDropDownClassName={textColorIsWhite ? styles.editDropDown : undefined}
                                showViewMore={false}
                    />
                    <div className={styles.messageTransitionContainer}>
                        <CSSTransition nodeRef={transitionRef} appear classNames={transitionClassNames} in={props.aboveTheFold}
                                       timeout={500}>
                            <div ref={transitionRef} className={messageContainerClassName}>
                                <div className={styles.contentContainer}>
                                    <Scrollbar
                                        scrollbarWidth={reactScrollBarsCustomWidth}
                                        disableTrackYWidthCompensation={true}
                                        wrapperProps={{
                                            style: {width: "90%"},
                                        }}
                                        thumbYProps={{
                                            style: {background: statementBackgroundObject[statementColor].textColor || undefined},
                                        }}
                                        trackYProps={{
                                            style: {width: 6}
                                        }}
                                    >
                                        <Markdown source={props.blockObj.json_data.statement?.text} withStyles={true}/>
                                    </Scrollbar>
                                </div>
                                {props.blockObj.json_data.statement?.buttonData.buttonToggle && <div className={styles.linkButtonContainer}>
                                    <CTAButton className={styles.callToActionLink} buttonData={props.blockObj.json_data.statement?.buttonData}>
                                        <span>{props.blockObj.json_data.statement?.buttonData.buttonText}</span>
                                    </CTAButton>
                                </div>}
                                {mediaObjectUrl && <ImageComponent src={getCdnUrl(mediaObjectUrl)}
                                     className={styles.statementMobileImage} alt={mediaObject?.alt}/>}
                            </div>
                        </CSSTransition>
                    </div>
                </div>
            </>}
        </section>
    );
});

export default StatementBlock;
